<template>
  <div class="ls-google-map" :id="`ls-map-${inputId}`"></div>
</template>

<script>
  import { Loader } from '@googlemaps/js-api-loader';
  import { v4 as uuid } from 'uuid';
  import { onMounted, reactive, ref, watch } from 'vue';

  export default {
    name: 'GoogleMap',
    props: {
      apiKey: {
        type: String,
        required: true,
      },
      center: {
        type: Object,
        required: true,
      },
      initialMarker: {
        type: Boolean,
        required: false,
        default: true,
      },
      markers: {
        type: Array,
        required: false,
        default: () => [],
      },
      zoom: {
        type: Number,
        required: false,
        default: 6,
        validator: (value) => {
          return value >= 0 && value <= 21;
        },
      },
      icon: {
        type: Object,
        required: false,
        default: () => {
          return {
            path: "M46,5.1v35.8c0,2.9-2.2,5.1-5.1,5.1H30.7l-7.5,12l-7.5-12H5.1C2.2,46,0,43.7,0,40.9V5.1C0,2.2,2.2,0,5.1,0h35.8C43.8, 0, 46, 2.2, 46, 5.1z",
            fillColor: "#EE3124",
            strokeWeight: 0,
            fillOpacity: 1,
            labelOrigin: {
              x: 22,
              y: 22,
            }
          }
        }
      },
      enableIconEvents: {
        type: Boolean,
        required: false,
        default: true
      }
    },
    setup(props, { emit }) {
      const inputId = ref(uuid());
      // MAP
      let map = undefined;
      const loader = new Loader({
        apiKey: props.apiKey,
        version: 'weekly',
        libraries: ['places'],
      });
      const mapOptions = reactive({
        center: props.center,
        disableDefaultUI: true,
        zoom: 9,
        maxZoom: 9,
      });

      const initMap = () => {
        loader
          .load()
          .then(() => {
            const mapElement = document.getElementById(`ls-map-${inputId.value}`);
            map = new google.maps.Map(mapElement, mapOptions);

            if (props.initialMarker) {
              createMarker({ position: props.center });
            }
            props.markers.forEach((marker, index) => {
              createMarker(marker, index + 1);
            });
          })
          .catch((e) => {
            console.error(e);
          });
      };
      const fitMapToMarkers = () => {
        if (map) {
          const mapBounds = new google.maps.LatLngBounds();
          for (var i = 0; i < liveMarkers.length; i++) {
            mapBounds.extend(liveMarkers[i].position);
          }

          map.fitBounds(mapBounds);
          if (map.getZoom() > 17) {
            map.setZoom(12);
          }
        }
      };
      const moveToLocation = () => {
        if (map) {
          const center = new google.maps.LatLng(props.center);
          map.panTo(center);
          map.setZoom(11);
        }
      };
      watch(
        () => props.center,
        (newVal, oldVal) => {
          if (newVal.lat !== oldVal.lat || newVal.lng !== oldVal.lng) {
            moveToLocation();
          }
        }
      );

      // MARKERS
      const liveMarkers = [];
      const createMarker = (marker, number = null) => {
        if (!map) return;
        const markerLabel = number ? createMarkerLabel(number) : null;
        const newMarker = new google.maps.Marker({
          ...marker,
          icon: props.icon,
          title: marker.locationInfo ? marker.locationInfo.title : '',
          label: markerLabel,
          optimized: false,
          map,
          zIndex: number,
        });
        if (marker.locationInfo && props.enableIconEvents) {
          newMarker.addListener('click', () => emit('setActive', newMarker.id));
          newMarker.addListener('mouseover', () => hoverIn(newMarker));
          newMarker.addListener('mouseout', () => hoverOut(newMarker));
        }
        liveMarkers.push(newMarker);
      };
      const updateMarkers = () => {
        liveMarkers.forEach((marker) => {
          marker.setMap(null);
        });
        liveMarkers.length = 0;

        props.markers.forEach((marker, index) => {
          createMarker(marker, index + 1);
        });

        if (liveMarkers.length) {
          fitMapToMarkers();
        }
      };
      const createMarkerLabel = (number) => {
        return {
          background: '#EE3124',
          color: '#fff',
          fontSize: '26px',
          text: `${number}`,
          transition: '0.2s linear all'
        };
      };
      const hoverIn = (marker) => {
        const hoverMarker = {
          path: "M46,5.1v35.8c0,2.9-2.2,5.1-5.1,5.1H30.7l-7.5,12l-7.5-12H5.1C2.2,46,0,43.7,0,40.9V5.1C0,2.2,2.2,0,5.1,0h35.8C43.8, 0, 46, 2.2, 46, 5.1z",
          fillColor: "#b12722",
          strokeWeight: 0,
          fillOpacity: 1,
          scale: 1.1,
          labelOrigin: {
            x: 22,
            y: 22,
          }
        };
        marker.label.fontSize = '32px';
        marker.label.fontWeight = 'bold';
        marker.zIndex += 100;
        marker.setIcon(hoverMarker);
      };
      const hoverOut = (marker) => {
        marker.label.fontSize = '26px';
        marker.label.fontWeight = 'normal';
        marker.zIndex -= 100;
        marker.setIcon(props.icon);
      };
      watch(
        () => props.markers,
        () => updateMarkers()
      );

      onMounted(() => {
        initMap();
      });

      return { inputId };
    },
  };
</script>
